import React, { useState, useEffect, useRef } from 'react';
import { Plus, X, Search, MessageSquare, User, ChevronDown, ChevronUp, Send, Clock, CheckCircle, AlertCircle, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';
import apiService from './services/api';
import { Panel, Badge, Button, LoadingSpinner } from './components';

const CATEGORIES = [
  { value: 'complaint', label: 'Complaint', color: 'bg-red-100 text-red-800' },
  { value: 'refund', label: 'Refund', color: 'bg-purple-100 text-purple-800' },
  { value: 'cancellation_fee', label: 'Cancellation Fee', color: 'bg-orange-100 text-orange-800' },
  { value: 'scheduling', label: 'Scheduling', color: 'bg-blue-100 text-blue-800' },
  { value: 'payment', label: 'Payment', color: 'bg-green-100 text-green-800' },
  { value: 'policy', label: 'Policy', color: 'bg-cyan-100 text-cyan-800' },
  { value: 'system', label: 'System', color: 'bg-gray-100 text-gray-800' },
  { value: 'event', label: 'Event', color: 'bg-pink-100 text-pink-800' },
  { value: 'other', label: 'Other', color: 'bg-gray-100 text-gray-600' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-600' },
  { value: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'high', label: 'High', color: 'bg-red-100 text-red-800' },
  { value: 'urgent', label: 'Urgent', color: 'bg-red-200 text-red-900' },
];

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-gray-800 text-white', icon: AlertCircle },
  pending: { label: 'Pending', color: 'bg-red-600 text-white', icon: Clock },
  review: { label: 'Review', color: 'bg-blue-600 text-white', icon: Eye },
  resolved: { label: 'Resolved', color: 'bg-green-600 text-white', icon: CheckCircle },
};

const getCategoryStyle = (cat) => CATEGORIES.find(c => c.value === cat) || CATEGORIES[CATEGORIES.length - 1];
const getPriorityStyle = (pri) => PRIORITIES.find(p => p.value === pri) || PRIORITIES[0];

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function CommsPage() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ open: 0, pending: 0, review: 0, resolved: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('active');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [expandedDetail, setExpandedDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  // Client search for form
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const clientTimeout = useRef(null);

  const [formData, setFormData] = useState({
    title: '', content: '', category: 'other', priority: 'medium',
    assigned_to: '', client: null, appointment: null,
  });

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter === 'resolved') params.status = 'resolved';
      else if (statusFilter !== 'all' && statusFilter !== 'active') params.status = statusFilter;
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (searchTerm) params.search = searchTerm;
      params.ordering = '-created_at';

      const [itemsRes, countsRes, assigneesRes] = await Promise.all([
        apiService.getCommItems(params),
        apiService.getCommCounts(),
        apiService.get('/amn/team-tasks/assignees/'),
      ]);
      let allItems = itemsRes.results || itemsRes || [];
      if (statusFilter === 'active') {
        allItems = allItems.filter(i => i.status !== 'resolved');
      }
      setItems(allItems);
      setCounts(countsRes);
      setTeamMembers(assigneesRes || []);
    } catch (err) {
      console.error('Failed to load comms:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [statusFilter, categoryFilter]);

  // Debounced search
  const searchTimeout = useRef(null);
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => loadData(), 400);
  }, [searchTerm]);

  // Client search for form
  useEffect(() => {
    if (clientTimeout.current) clearTimeout(clientTimeout.current);
    if (clientSearch.length < 2) { setClientResults([]); return; }
    clientTimeout.current = setTimeout(async () => {
      try {
        const data = await apiService.get('/amn/clients/', { search: clientSearch, page_size: 8, ordering: '-appointment_count' });
        setClientResults(data.results || data);
      } catch (err) { console.error(err); }
    }, 300);
  }, [clientSearch]);

  const resetForm = () => {
    setFormData({ title: '', content: '', category: 'other', priority: 'medium', assigned_to: '', client: null, appointment: null });
    setSelectedClient(null);
    setClientSearch('');
    setEditingItem(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.content.trim()) return;
    try {
      const payload = { ...formData, assigned_to: formData.assigned_to || null, client: selectedClient?.id || null };
      if (editingItem) {
        await apiService.updateCommItem(editingItem.id, payload);
      } else {
        await apiService.createCommItem(payload);
      }
      resetForm();
      loadData();
    } catch (err) {
      alert(err.message || 'Failed to save');
    }
  };

  const handleStatusChange = async (item, newStatus) => {
    try {
      await apiService.updateCommItem(item.id, { status: newStatus });
      loadData();
      if (expandedId === item.id) fetchDetail(item.id);
    } catch (err) { console.error(err); }
  };

  const fetchDetail = async (id) => {
    setLoadingDetail(true);
    try {
      const data = await apiService.getCommItem(id);
      setExpandedDetail(data);
    } catch (err) { console.error(err); }
    finally { setLoadingDetail(false); }
  };

  const toggleExpand = (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      setExpandedDetail(null);
      setReplyText('');
    } else {
      setExpandedId(id);
      setReplyText('');
      fetchDetail(id);
    }
  };

  const handleReply = async (itemId) => {
    if (!replyText.trim()) return;
    setSendingReply(true);
    try {
      await apiService.createCommReply({ comm_item: itemId, content: replyText.trim() });
      setReplyText('');
      fetchDetail(itemId);
      loadData();
    } catch (err) { alert(err.message || 'Failed to send reply'); }
    finally { setSendingReply(false); }
  };

  const startEdit = (item) => {
    setFormData({
      title: item.title, content: expandedDetail?.content || '', category: item.category,
      priority: item.priority, assigned_to: expandedDetail?.assigned_to || '', client: item.client,
    });
    setSelectedClient(item.client ? { id: item.client, person: { full_name: item.client_name } } : null);
    setEditingItem(item);
    setShowForm(true);
  };

  const activeCount = counts.open + counts.pending + counts.review;

  return (
    <div className="flex-1 bg-neutral-50 overflow-y-auto p-3" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">CC Board</h1>
          <p className="text-xs text-gray-500">{activeCount} active items</p>
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} size="sm">
          <Plus className="w-3.5 h-3.5 mr-1" />
          New Item
        </Button>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <Panel className="mb-3 p-3">
          <form onSubmit={handleSubmit} className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold text-gray-700">{editingItem ? 'Edit Item' : 'New Item'}</h3>
              <button type="button" onClick={resetForm} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
            </div>
            <input
              type="text" value={formData.title}
              onChange={e => setFormData({ ...formData, title: e.target.value })}
              placeholder="Title / Subject" required autoFocus
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
            />
            <textarea
              value={formData.content}
              onChange={e => setFormData({ ...formData, content: e.target.value })}
              placeholder="Details..." rows={4} required
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
            />
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-0.5">Category</label>
                <select value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-800">
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-0.5">Priority</label>
                <select value={formData.priority} onChange={e => setFormData({ ...formData, priority: e.target.value })}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-800">
                  {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-0.5">Assign To</label>
                <select value={formData.assigned_to} onChange={e => setFormData({ ...formData, assigned_to: e.target.value })}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-800">
                  <option value="">Unassigned</option>
                  {teamMembers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
            </div>
            {/* Client Link */}
            <div>
              <label className="block text-xs text-gray-500 mb-0.5">Link Client (optional)</label>
              {selectedClient ? (
                <div className="flex items-center justify-between bg-gray-50 rounded px-2 py-1.5">
                  <span className="text-xs font-medium">{selectedClient.person?.full_name || selectedClient.person?.first_name}</span>
                  <button type="button" onClick={() => { setSelectedClient(null); setFormData({...formData, client: null}); }} className="text-xs text-red-600">Remove</button>
                </div>
              ) : (
                <div className="relative">
                  <input type="text" value={clientSearch} onChange={e => setClientSearch(e.target.value)}
                    placeholder="Search client..." className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-800" />
                  {clientResults.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg max-h-32 overflow-y-auto">
                      {clientResults.map(c => (
                        <button key={c.id} type="button"
                          onClick={() => { setSelectedClient(c); setClientSearch(''); setClientResults([]); setFormData({...formData, client: c.id}); }}
                          className="w-full text-left px-2 py-1.5 text-xs hover:bg-gray-50 border-b border-gray-100 last:border-0">
                          {c.person?.first_name} {c.person?.last_name}
                          {c.person?.phone_number && <span className="text-gray-400 ml-1">{c.person.phone_number}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-1">
              <button type="button" onClick={resetForm} className="px-3 py-1 text-xs text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
              <button type="submit" className="px-3 py-1 text-xs text-white bg-red-800 rounded hover:bg-red-900">{editingItem ? 'Update' : 'Create'}</button>
            </div>
          </form>
        </Panel>
      )}

      {/* Filter Bar */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="flex border-b border-gray-200">
          {[
            { key: 'active', label: `Active (${activeCount})` },
            { key: 'resolved', label: `Resolved (${counts.resolved})` },
            { key: 'all', label: `All (${counts.total})` },
          ].map(tab => (
            <button key={tab.key} onClick={() => setStatusFilter(tab.key)}
              className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${statusFilter === tab.key ? 'border-red-800 text-red-800' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {tab.label}
            </button>
          ))}
        </div>
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
          className="px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800">
          <option value="all">All Categories</option>
          {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <div className="relative flex-1 min-w-[150px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search..." className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800" />
        </div>
      </div>

      {/* Items List */}
      {loading ? (
        <div className="py-12"><LoadingSpinner /></div>
      ) : items.length === 0 ? (
        <Panel className="py-12 text-center">
          <MessageSquare className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          <p className="text-sm text-gray-500">No items found</p>
          <button onClick={() => { resetForm(); setShowForm(true); }} className="mt-2 text-xs text-red-800 hover:underline">Create your first item</button>
        </Panel>
      ) : (
        <div className="space-y-2">
          {items.map(item => {
            const catStyle = getCategoryStyle(item.category);
            const priStyle = getPriorityStyle(item.priority);
            const statusConf = STATUS_CONFIG[item.status] || STATUS_CONFIG.open;
            const isExpanded = expandedId === item.id;

            return (
              <Panel key={item.id} className="!p-0 overflow-hidden">
                {/* Item Header */}
                <div className="px-3 py-2.5 cursor-pointer hover:bg-gray-50" onClick={() => toggleExpand(item.id)}>
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`text-sm font-medium ${item.status === 'resolved' ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                          {item.title}
                        </span>
                        <span className={`inline-flex px-1.5 py-0 rounded text-[10px] font-medium ${catStyle.color}`}>{catStyle.label}</span>
                        {item.priority !== 'medium' && (
                          <span className={`inline-flex px-1.5 py-0 rounded text-[10px] font-medium ${priStyle.color}`}>{priStyle.label}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-400">
                        {item.created_by_name && <span>{item.created_by_name}</span>}
                        <span>{timeAgo(item.created_at)}</span>
                        {item.assigned_to_name && <span>→ {item.assigned_to_name}</span>}
                        {item.client_name && (
                          <Link to={`/clients/${item.client}`} className="text-red-700 hover:underline" onClick={e => e.stopPropagation()}>
                            {item.client_name}
                          </Link>
                        )}
                        {item.reply_count > 0 && (
                          <span className="flex items-center gap-0.5"><MessageSquare className="w-3 h-3" />{item.reply_count}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <select
                        value={item.status}
                        onChange={e => { e.stopPropagation(); handleStatusChange(item, e.target.value); }}
                        onClick={e => e.stopPropagation()}
                        className={`text-[10px] font-medium rounded px-1.5 py-0.5 border-0 cursor-pointer ${statusConf.color}`}
                      >
                        <option value="open">Open</option>
                        <option value="pending">Pending</option>
                        <option value="review">Review</option>
                        <option value="resolved">Resolved</option>
                      </select>
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="border-t border-gray-100 bg-gray-50">
                    {loadingDetail && !expandedDetail ? (
                      <div className="p-4 text-center text-xs text-gray-500">Loading...</div>
                    ) : expandedDetail && expandedDetail.id === item.id ? (
                      <div className="p-3 space-y-3">
                        {/* Full content */}
                        <div className="bg-white rounded p-3 text-xs text-gray-700 whitespace-pre-wrap">{expandedDetail.content}</div>

                        {/* Replies */}
                        {expandedDetail.replies && expandedDetail.replies.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-[10px] font-semibold text-gray-500 uppercase">Replies ({expandedDetail.replies.length})</p>
                            {expandedDetail.replies.map(reply => (
                              <div key={reply.id} className="bg-white rounded p-2.5 border-l-2 border-red-200">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs font-medium text-gray-800">{reply.author_name || 'Unknown'}</span>
                                  <span className="text-[10px] text-gray-400">{timeAgo(reply.created_at)}</span>
                                </div>
                                <p className="text-xs text-gray-700 whitespace-pre-wrap">{reply.content}</p>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Reply input */}
                        <div className="flex gap-2">
                          <textarea
                            value={replyText}
                            onChange={e => setReplyText(e.target.value)}
                            placeholder="Write a reply..."
                            rows={2}
                            className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                          />
                          <button
                            onClick={() => handleReply(item.id)}
                            disabled={!replyText.trim() || sendingReply}
                            className="self-end px-3 py-1.5 text-xs text-white bg-red-800 rounded hover:bg-red-900 disabled:opacity-50 flex items-center gap-1"
                          >
                            <Send className="w-3 h-3" />
                            {sendingReply ? '...' : 'Reply'}
                          </button>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                          <button onClick={() => startEdit(item)} className="text-xs text-gray-500 hover:text-gray-700">Edit</button>
                          {item.status !== 'resolved' && (
                            <button onClick={() => handleStatusChange(item, 'resolved')} className="text-xs text-green-600 hover:text-green-800">Mark Resolved</button>
                          )}
                          {item.status === 'resolved' && (
                            <button onClick={() => handleStatusChange(item, 'open')} className="text-xs text-blue-600 hover:text-blue-800">Reopen</button>
                          )}
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}
              </Panel>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default CommsPage;
